import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import Button from "ui/Button";
import Callout from "ui/Callout";
import Loading from "ui/Loading";
import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import PreviewDiff from "features/Server/ServerGroupPreviewDiff";
import ServerGroupForm, {
  emptyTagRule,
  enteredRules,
  includableGroups,
  MatchRule,
  ruleMatchedIds,
  selectableServers,
  toMatchRuleInput,
  validationError,
} from "features/Server/ServerGroupForm";
import { type SelectableGroup } from "features/Server/ServerGroupForm/GroupPicker";
import { type SelectableServer } from "features/Server/ServerGroupForm/ServerPicker";
import { useServerGroupFormOptionsQuery } from "features/Server/ServerGroupForm/gql/Query.generated";
import { useServerGroupMembers } from "features/Server/ServerGroupForm/useServerGroupMembers";
import SettingsNav from "ui/SettingsNav";
import { useCurrentOrganization } from "providers/WithCurrentOrganization";
import { useSetFlash, useSetNextFlashLocation } from "providers/WithFlashes";
import { useRoutes } from "utils/routes";

import { useCreateServerGroupMutation } from "features/Server/ServerGroupNew/gql/Mutation.create.generated";

const ServerGroupNew = () => {
  const currentOrganization = useCurrentOrganization();
  const { organization } = useRoutes();

  if (currentOrganization == null) {
    return <Loading error />;
  }

  if (!currentOrganization.enabledFeatures.serverGroupCrud) {
    return <Navigate to={organization(currentOrganization.slug)} replace />;
  }

  // Creating is org manage (ServerGroupPolicy#create?), which a modify-only member does not have.
  // The nav offers Add Server Group to them all the same, so say why rather than bouncing them.
  if (!currentOrganization.permittedToCreateServerGroups) {
    return (
      <PageContent
        title="Create server group"
        pageCategory="servergroup"
        pageName="new"
        featureNav={<SettingsNav />}
      >
        <Panel title="Create server group">
          <PanelSection>
            You are not allowed to create new server groups because you do not
            have the <strong>Manage</strong> permission on this organization.
          </PanelSection>
        </Panel>
      </PageContent>
    );
  }

  return (
    <PageContent
      title="Create server group"
      pageCategory="servergroup"
      pageName="new"
      featureNav={<SettingsNav />}
    >
      <Callout className="mb-4" variant="info" title="What is a server group?">
        A server group is a collection of servers. Match them automatically with
        rules like tag or server name, pick servers directly, or include other
        server groups. Use it to view and manage part of your fleet as a single
        unit across pganalyze.
      </Callout>
      <ServerGroupNewLoader
        organizationSlug={currentOrganization.slug}
        servers={selectableServers(currentOrganization)}
      />
    </PageContent>
  );
};

// The group picker's options cannot seed form state while they are still loading, so the
// query resolves before the form mounts. Servers come from the nav query already in context.
const ServerGroupNewLoader = ({
  organizationSlug,
  servers,
}: {
  organizationSlug: string;
  servers: SelectableServer[];
}) => {
  const { data, loading, error } = useServerGroupFormOptionsQuery({
    variables: { organizationSlug },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <ServerGroupNewContent
      organizationSlug={organizationSlug}
      servers={servers}
      serverGroups={includableGroups(data.getServerGroups)}
    />
  );
};

const ServerGroupNewContent = ({
  organizationSlug,
  servers,
  serverGroups,
}: {
  organizationSlug: string;
  servers: SelectableServer[];
  serverGroups: SelectableGroup[];
}) => {
  const navigate = useNavigate();
  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const { organizationServerGroupsSettings, serverGroupEdit } = useRoutes();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [matchRules, setMatchRules] = useState<MatchRule[]>([emptyTagRule()]);
  const [memberServerIds, setMemberServerIds] = useState<string[]>([]);
  const [memberServerGroupIds, setMemberServerGroupIds] = useState<string[]>(
    [],
  );
  const [createServerGroup, { loading }] = useCreateServerGroupMutation();

  const candidateMatchRules = toMatchRuleInput(enteredRules(matchRules));
  const {
    baselineMembers,
    previewMembers,
    lastKnownPreviewMembers,
    loading: membersLoading,
    error: membersError,
  } = useServerGroupMembers({
    organizationSlug,
    matchRules: candidateMatchRules,
    memberServerIds,
    memberServerGroupIds,
  });
  // Holding the last answer while the next one loads keeps the locks from dropping and coming back
  const ruleMatchedServerIds = ruleMatchedIds(lastKnownPreviewMembers);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const invalidReason = validationError({
    name,
    matchRules,
    memberServerIds,
    memberServerGroupIds,
  });
  const saveDisabled = loading || invalidReason != null;

  const handleSave = () => {
    // validate_membership! rejects a rule-matched server as an explicit member, and its checkbox is
    // locked, so keeping it would leave a save that can never succeed. Filter on a settled answer
    // only: acting on a stale one would silently drop a member, where sending it just fails visibly.
    const ruleMatched = new Set(ruleMatchedIds(previewMembers));
    createServerGroup({
      variables: {
        organizationSlug,
        name: trimmedName,
        description: trimmedDescription === "" ? null : trimmedDescription,
        matchRules: candidateMatchRules,
        memberServerIds: memberServerIds.filter((id) => !ruleMatched.has(id)),
        memberServerGroupIds,
      },
      // The edit page we navigate to seeds its pickers from ServerGroupFormOptions, which this
      // page already put in the cache without the new group
      refetchQueries: ["WithCurrentOrganization", "ServerGroupFormOptions"],
      awaitRefetchQueries: true,
      onError: (error) => {
        setFlash("error", error.message);
      },
      onCompleted: (data) => {
        const newGroup = data.createServerGroup;
        setFlash("success", "Server group created");
        setNextFlashLocation(serverGroupEdit(newGroup.id));
        navigate(serverGroupEdit(newGroup.id));
      },
    });
  };

  const handleCancel = () => {
    navigate(organizationServerGroupsSettings(organizationSlug));
  };

  return (
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <ServerGroupForm
          name={name}
          description={description}
          matchRules={matchRules}
          servers={servers}
          serverGroups={serverGroups}
          memberServerIds={memberServerIds}
          memberServerGroupIds={memberServerGroupIds}
          ruleMatchedServerIds={ruleMatchedServerIds}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onMatchRulesChange={setMatchRules}
          onMemberServerIdsChange={setMemberServerIds}
          onMemberServerGroupIdsChange={setMemberServerGroupIds}
          footer={
            <Panel title="Create server group">
              <PanelSection className="flex items-center gap-2">
                <Button
                  bare
                  onClick={handleSave}
                  className="btn btn-success"
                  disabled={saveDisabled}
                  title={invalidReason ?? undefined}
                >
                  Create Server Group
                </Button>
                <Button bare onClick={handleCancel} variant="link">
                  Cancel
                </Button>
              </PanelSection>
            </Panel>
          }
        />
      </div>
      <div className="w-[390px] shrink-0">
        <PreviewDiff
          baselineMembers={baselineMembers}
          previewMembers={previewMembers}
          loading={membersLoading}
          error={membersError}
        />
      </div>
    </div>
  );
};

export default ServerGroupNew;
